import { useCallback, useContext, useMemo, useState } from 'react';
import { Button, OverlaysProvider, Tooltip } from '@blueprintjs/core';
import { lock_hooks } from '@axo/shared/data-access/hooks';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { useCloseApplication } from '../../../hooks/navigation/useCloseApplication';
import { useAssignments } from '../../../hooks/utils/useAssignments';
import { useMarkAsProcessed } from '../../../hooks/utils/useMarkAsProcessed';
import { FollowUpModal } from '../../FollowUp/FollowUpModal';
import { EventCodeACE } from '../../EventLogWrapper/event-code';

export function SetProcessedButton({
  marketCountry,
  applicationID,
  isSecuredLoan = false,
}: {
  marketCountry: string;
  applicationID: string;
  isSecuredLoan?: boolean;
}) {
  const [isFollowUpModalVisible, setIsFollowUpModalVisible] = useState(false);

  const { state } = useContext(DataAccessContext);

  const log = useEventLogger({
    Type: KnownResourceType.LoanApplication,
    ID: applicationID,
  });

  const { assignee, processedAt, isSuccess } = useAssignments({
    applicationID,
    marketCountry,
  });

  const { markAsProcessed, isProcessing } = useMarkAsProcessed({
    applicationID,
    marketCountry,
  });

  const close = useCloseApplication(marketCountry);

  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    marketCountry
  );

  const buttonState = useMemo(() => {
    if (!isSuccess || !hasLock.isSuccess) {
      return {
        disabled: true,
        text: 'Loading status',
      };
    }

    if (processedAt) {
      return {
        disabled: true,
        text: 'Application is already marked as processed.',
      };
    }

    if (assignee !== state.user.clientID) {
      return {
        disabled: true,
        text: 'Application can only be set as processed by the assigned user.',
      };
    }

    return {
      disabled: false,
      text: '',
    };
  }, [
    isSuccess,
    hasLock.isSuccess,
    processedAt,
    assignee,
    state.user.clientID,
  ]);

  const processAndClose = useCallback(async () => {
    if (isFollowUpModalVisible) setIsFollowUpModalVisible(false);

    await markAsProcessed();

    log(EventCodeACE.ProcessApplication);

    await close.closeAndUnlock(applicationID);
  }, [applicationID, markAsProcessed, log, close, isFollowUpModalVisible]);

  const onProcessButtonClick = useCallback(async () => {
    if (isSecuredLoan) {
      setIsFollowUpModalVisible(true);
      return;
    }

    await processAndClose();
  }, [isSecuredLoan, processAndClose]);

  const onFollowUpModalClose = useCallback(() => {
    setIsFollowUpModalVisible(false);
  }, [setIsFollowUpModalVisible]);

  return (
    <OverlaysProvider>
      <Tooltip
        disabled={!buttonState.disabled}
        content={<span>{buttonState.text}</span>}
      >
        <Button
          intent="primary"
          icon="automatic-updates"
          disabled={buttonState.disabled}
          onClick={onProcessButtonClick}
          loading={isProcessing}
        >
          Mark as processed
        </Button>
      </Tooltip>

      {isFollowUpModalVisible && isSecuredLoan && (
        <FollowUpModal
          onClose={onFollowUpModalClose}
          onSubmit={processAndClose}
        />
      )}
    </OverlaysProvider>
  );
}
